import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AIAssistant from "@/components/AIAssistant";
import {
  LayoutDashboard, Target, Route as RouteIcon, PhoneCall, MessageSquare,
  AlertTriangle, IndianRupee, Settings as SettingsIcon, Users, Bell,
  HeartPulse, LogOut, Sparkles, TrendingUp, Stethoscope, Percent, Building2,
  PhoneOutgoing, UserPlus, Quote, Flag, Menu
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/team-goals", label: "Team Goals", icon: Flag },
  { to: "/goals", label: "Clinic Goals", icon: Target },
  { to: "/journey", label: "Patient Journey", icon: RouteIcon, roles: ["Operational Manager", "Manager", "Relationship Executive"] },
  { to: "/followups", label: "Follow-Ups", icon: PhoneCall, roles: ["Operational Manager", "Manager"] },
  { to: "/calls", label: "Calls Log", icon: PhoneOutgoing, roles: ["Relationship Executive"] },
  { to: "/dues", label: "Dues", icon: IndianRupee, roles: ["Relationship Executive"] },
  { to: "/feedback", label: "Feedback", icon: MessageSquare, roles: ["Operational Manager", "Manager", "Relationship Executive"] },
  { to: "/concerns", label: "Concerns", icon: AlertTriangle, roles: ["Operational Manager", "Manager", "Relationship Executive"] },
  { to: "/referrals", label: "Referrals", icon: UserPlus, roles: ["Operational Manager", "Manager", "Relationship Executive"] },
  { to: "/upsells", label: "Upsell CrossSell", icon: TrendingUp, roles: ["Operational Manager", "Manager", "Relationship Executive"] },
  { to: "/testimonials", label: "Testimonials", icon: Quote, roles: ["Operational Manager", "Manager", "Relationship Executive"] },
  { to: "/complications", label: "Complications", icon: Stethoscope, roles: ["Operational Manager", "Manager"] },
  { to: "/pricing", label: "Pricing Discounts", icon: Percent, roles: ["Operational Manager", "Manager"] },
  { to: "/lookfeel", label: "Look and Feel", icon: Building2, roles: ["Operational Manager", "Manager"] },
  { to: "/team", label: "Team", icon: Users },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branch, setBranch, meta, notifications, team, person, setPerson, currentRole, dateRange, setPreset, setCustomRange } = useApp();
  const [aiOpen, setAiOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const visibleNav = NAV.filter((i) => !i.roles || !currentRole || currentRole === "Admin" || i.roles.includes(currentRole));

  return (
    <div className="min-h-screen flex bg-[#0F172A] relative z-10">
      {/* Sidebar */}
      <aside className={`w-64 shrink-0 border-r border-white/5 bg-[#0F172A] flex flex-col fixed h-screen z-40 transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="h-16 flex items-center gap-2 px-6 border-b border-white/5">
          <HeartPulse className="h-6 w-6 text-[#C9A24B]" />
          <div className="leading-tight">
            <div className="font-display text-white text-lg">Youngness</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500">RM CRM</div>
          </div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {visibleNav.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <button
                key={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => { navigate(item.to); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  active
                    ? "border-[#C9A24B] text-white bg-[#1E293B]"
                    : "border-transparent text-slate-400 hover:text-white hover:bg-[#1E293B]/50"
                }`}
              >
                <Icon className={`h-4 w-4 ${active ? "text-[#C9A24B]" : ""}`} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/5">
          <button
            data-testid="ai-assistant-open"
            onClick={() => setAiOpen(true)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#C9A24B]/10 text-[#C9A24B] hover:bg-[#C9A24B]/20 transition-colors text-sm font-medium"
          >
            <Sparkles className="h-4 w-4" /> AI Assistant
          </button>
        </div>
      </aside>
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black/50 z-30 lg:hidden" />}

      {/* Main */}
      <div className="flex-1 lg:ml-64 ml-0 flex flex-col min-h-screen">
        {/* Header */}
        <header className="min-h-16 sticky top-0 z-20 border-b border-white/5 bg-[#0F172A]/80 backdrop-blur-xl flex items-center justify-between gap-2 px-4 lg:px-6 py-2 flex-wrap">
          <button data-testid="sidebar-toggle" onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-[#1E293B] text-slate-300">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger data-testid="branch-selector" className="w-40 bg-[#1E293B] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                <SelectItem value="All">All Branches</SelectItem>
                {meta.branches?.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={person || "none"} onValueChange={(v) => setPerson(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="person-selector" className="w-44 bg-[#1E293B] border-white/10 text-white">
                <SelectValue placeholder="Select your name" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                <SelectItem value="none">All People</SelectItem>
                {team?.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} · {t.role}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={dateRange.preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="daterange-selector" className="w-36 bg-[#1E293B] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                {["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month", "All", "Custom"].map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {dateRange.preset === "Custom" || true ? (
              <div className="hidden xl:flex items-center gap-1">
                <input data-testid="date-from" type="date" value={dateRange.from || ""} onChange={(e) => setCustomRange(e.target.value, dateRange.to)}
                  className="bg-[#1E293B] border border-white/10 rounded-md text-white text-xs px-2 h-9" />
                <span className="text-slate-600 text-xs">→</span>
                <input data-testid="date-to" type="date" value={dateRange.to || ""} onChange={(e) => setCustomRange(dateRange.from, e.target.value)}
                  className="bg-[#1E293B] border border-white/10 rounded-md text-white text-xs px-2 h-9" />
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="notification-bell" className="relative p-2 rounded-lg hover:bg-[#1E293B] transition-colors">
                  <Bell className="h-5 w-5 text-slate-300" />
                  {notifications.count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-[#DC2626] text-white text-[10px] flex items-center justify-center font-bold">
                      {notifications.count > 9 ? "9+" : notifications.count}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 bg-[#1E293B] border-white/10 text-white p-0">
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <span className="font-medium">Notifications</span>
                  <Badge className="bg-[#DC2626]/20 text-[#DC2626] border-0">{notifications.count} active</Badge>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.items?.length === 0 && (
                    <div className="p-6 text-center text-slate-500 text-sm">All caught up. Nothing pending.</div>
                  )}
                  {notifications.items?.map((n, i) => (
                    <div key={i} data-testid="notification-item" className="p-3 border-b border-white/5 flex gap-3 hover:bg-[#0F172A]/50">
                      <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${
                        n.severity === "critical" ? "bg-[#DC2626]" : n.severity === "high" ? "bg-[#C9A24B]" : "bg-[#64748B]"
                      }`} />
                      <span className="text-sm text-slate-300 leading-snug">{n.text}</span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 hover:bg-[#1E293B] rounded-lg p-1 pr-3 transition-colors">
                  <Avatar className="h-8 w-8 ring-1 ring-[#C9A24B]/40">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#C9A24B] text-[#0F172A] text-xs">{user?.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden md:block">
                    <div className="text-sm text-white leading-none">{person || user?.name}</div>
                    <div className="text-[10px] text-[#C9A24B] uppercase tracking-wider mt-0.5">{team?.find((t) => t.name === person)?.role || (user?.role === "founder" ? "Admin" : "Team")}</div>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 bg-[#1E293B] border-white/10 text-white">
                <div className="text-sm text-slate-400 mb-1">{user?.email}</div>
                <Button data-testid="logout-button" onClick={logout} variant="ghost" className="w-full justify-start text-[#DC2626] hover:text-[#DC2626] hover:bg-[#DC2626]/10 mt-2">
                  <LogOut className="h-4 w-4 mr-2" /> Sign Out
                </Button>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8">{children}</main>
      </div>

      <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
